/**
 * Feature flag resolution — checks sources in priority order:
 *   1. localStorage override — `ff_<name>` → 'true' / 'false'
 *   2. Vite env var — `import.meta.env.VITE_FEATURE_<UPPER_SNAKE>` → 'true' / 'false'
 *   3. Per-flag default from `FLAG_DEFAULTS` below
 *   4. `false` for any flag not listed
 *
 * localStorage wins if set, letting testers toggle without redeploying.
 * Env var overrides the default but is overridden by localStorage.
 *
 * Use `FLAG_DEFAULTS` to promote a flag to default-on once the feature is
 * considered GA — this keeps the env var as an explicit escape hatch
 * (`VITE_FEATURE_<NAME>=false`) without forcing every deploy to set the var.
 */

/**
 * Default resolution when neither localStorage nor the env var is set.
 *
 * `inlineDeals`: the inline research desk / deals table / preview flow.
 * GA-by-default from 2026-04-24 — legacy workspace views will be removed
 * once this has baked; opt-out via `localStorage.setItem('ff_inlineDeals', 'false')`.
 */
const FLAG_DEFAULTS: Record<string, boolean> = {
  inlineDeals: true,
};

function toEnvVarKey(name: string): string {
  // camelCase → UPPER_SNAKE: e.g. "inlineDeals" → "INLINE_DEALS"
  return name.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toUpperCase();
}

/** Pure function — callable from non-React code (thunks, slices, etc.) */
export function getFeatureFlag(name: string): boolean {
  const localStorageKey = `ff_${name}`;
  const localValue = localStorage.getItem(localStorageKey);

  if (localValue === "true") return true;
  if (localValue === "false") return false;

  const envKey = `VITE_FEATURE_${toEnvVarKey(name)}`;
  const envValue = (import.meta.env as Record<string, string | undefined>)[envKey];

  if (envValue === "true") return true;
  if (envValue === "false") return false;

  return FLAG_DEFAULTS[name] ?? false;
}

/** React hook wrapper — use in components */
export function useFeatureFlag(name: string): boolean {
  return getFeatureFlag(name);
}
